import React, { memo, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import { ListHeightOffsetContextProvider } from '@cbgms/components/list-wrapper/ListWrapperUtils/ListHeightOffsetContext'
import ModulesRoutes from 'routes/ModulesRoutes'
import TopIFrame from 'app/menu/components/TopIFrame'
import SelectVehicleType from 'app/vehicles/components/SelectVehicleType/SelectVehicleTypeContextProvider'
import DuplicateVin from 'app/vehicles/components/DuplicateVehicleFound/DuplicateVinContext'
import { ShowNetPriceContextProvider } from './products/components/ProductListV2/ShowNetPriceContext'
import { SchedulerContextProvider } from 'app/scheduler/components/SchedulerContext'
import { CatalogSettingsContextProvider } from './settings/components/CatalogUsabilitySettings/CatalogSettingsContext'
import { TopBarContainer } from 'app/menu/components/TopBar/TopBarContainer'
import { IRootReducerState } from 'state/rootReducer'
import { SidebarMenu } from 'app/menu/components/Sidebar/Menu'
import { toggleSidebar } from './menu/state/actions'
import { ContentUtilsContextProvider } from 'app/ContentUtilsContext'
import { OrderAccountsContextProvider } from './workorder/contexts/OrderAccountsContextProvider'
import { StoredProductsContextProvider } from './products/components/ProductListV2/contexts/StoredProducts/StoredProductsContext'
import { DuplicateVehicleFoundModal } from './vehicles/components/DuplicateVehicleFound/DuplicateVehicleFoundModal'
import TelesalesWarningMessage from 'app/telesales/TelesalesWarningMessage'
import AppFooter from './footer/AppFooter'
import { DrawerHeaderContainer } from './menu/components/TopBar/DrawerHeader'
import NavigationLoggingMiddleware from 'contexts/Logging/NavigationLoggingMiddleware'
import WorkorderContextProvider from 'contexts/WorkorderContext/WorkorderContextProvider'
import { useTopBarHeight } from 'app/menu/components/TopBar/hooks/use-top-bar-height'
import { useIsIframeLimitedNavigation } from 'app/hooks/use-is-iframe-limited-navigation'
import ModalContainer from 'react-modal-promise'

const drawerWidthOpen = 220
const drawerWidthClosed = 60

const _Container: React.FC = () => {
  const sidebarOpen = useSelector<IRootReducerState, boolean>(state => state.menu.sidebarOpen)
  const sidebarVisible = useSelector<IRootReducerState, boolean>(state => state.menu.sidebarVisible)
  const iframeTopMenu = useIsIframeLimitedNavigation()
  const contentRef = React.useRef<HTMLDivElement | null>(null)

  // shop permissions are implicitly loaded from in the 'app\menu\components\TopBar\Shops.tsx'
  // we have to render that component and wait for the details to load before rendering the other modules.
  // of we don't do that, we might trigger permission-checks against the wrong data. causing 404's when refreshing the page
  const didLoadShopAndPermissions = useSelector<IRootReducerState, boolean>(state => state.auth.shop.ID === state.auth.currentShop)
  const dispatch = useDispatch()
  const handleDrawerOpenToggle = useCallback(() => dispatch(toggleSidebar()), [dispatch])
  const topBarHeight = useTopBarHeight()

  return (
    <Box display='flex'>
      <OrderAccountsContextProvider>
        <WorkorderContextProvider>
          <NavigationLoggingMiddleware /* inside OrderAccountContext to log selected order account */ />
          <SelectVehicleType>
            <DuplicateVin>
              <SchedulerContextProvider>
                <ContentUtilsContextProvider contentRef={contentRef}>
                  {!iframeTopMenu ? (
                    <TopBarContainer
                      drawerVisible={sidebarVisible}
                      drawerWidthOpen={drawerWidthOpen}
                      drawerWidthClosed={drawerWidthClosed}
                      open={sidebarOpen}
                    />
                  ) : (
                    <TopIFrame
                      drawerVisible={sidebarVisible}
                      drawerWidthOpen={drawerWidthOpen}
                      drawerWidthClosed={drawerWidthClosed}
                      open={sidebarOpen}
                    />
                  )}
                  {sidebarVisible && (
                    <SidebarMenu
                      drawerWidthOpen={drawerWidthOpen}
                      drawerWidthClosed={drawerWidthClosed}
                      drawerOpen={sidebarOpen}
                      onDrawerOpenToggle={handleDrawerOpenToggle}
                    />
                  )}
                  <Box
                    component='main'
                    ref={contentRef}
                    sx={{
                      flexGrow: 1,
                      maxWidth: `calc(100% - ${sidebarOpen ? drawerWidthOpen : drawerWidthClosed}px)`,
                      overflowY: 'clip' /* needs 'clip' to enable sticky headers. */,
                      overflowX: 'visible' /* needs 'visible' to allow 'wide' pages to scroll-X on body. */,
                      scrollBehavior: 'smooth',
                      transition: 'margin 300ms'
                    }}
                  >
                    <DrawerHeaderContainer />
                    <Box
                      id='content-root'
                      sx={{
                        minHeight: `calc(100vh - ${topBarHeight}px)`,
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1
                      }}
                    >
                      <DuplicateVehicleFoundModal />
                      <ModalContainer />
                      <StoredProductsContextProvider>
                        <CatalogSettingsContextProvider>
                          <ShowNetPriceContextProvider>
                            <ListHeightOffsetContextProvider value={0}>
                              <TelesalesWarningMessage />
                              {didLoadShopAndPermissions && <ModulesRoutes />}
                            </ListHeightOffsetContextProvider>
                          </ShowNetPriceContextProvider>
                        </CatalogSettingsContextProvider>
                      </StoredProductsContextProvider>
                    </Box>
                    <AppFooter />
                  </Box>
                </ContentUtilsContextProvider>
              </SchedulerContextProvider>
            </DuplicateVin>
          </SelectVehicleType>
        </WorkorderContextProvider>
      </OrderAccountsContextProvider>
    </Box>
  )
}

const Container = memo(_Container)
export default Container
